import React, { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StatCard } from "@/components/dashboard/StatCard";
import { EChartsSection } from "@/components/dashboard/EChartsSection";
import { DataTable } from "@/components/dashboard/DataTable";
import EChartsMap, { Warehouse, TransferOrder } from "./EChartsMap";
import { 
  ArrowLeft,
  RefreshCw,
  Loader2,
  AlertTriangle,
  LucideIcon
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// 通用报表模板接口
export interface ReportTemplateProps {
  // 页面配置
  title: string;
  subtitle: string;
  icon: LucideIcon;
  backPath?: string;
  
  // 数据状态
  isLoading: boolean;
  error?: Error | null;
  onRefresh: () => void;
  
  // KPI卡片数据
  kpiStats: {
    title: string;
    value: string;
    change: string;
    changeType: "positive" | "negative" | "neutral";
    icon: LucideIcon;
    onClick?: () => void;
  }[];
  
  // 状态信息
  statusInfo: {
    totalCount: number;
    efficiency?: number;
    customBadges?: {
      label: string;
      value: string;
      variant: "default" | "secondary" | "outline";
    }[];
  };
  
  // 图表数据
  chartData?: {
    typeDistribution: Array<{
      name: string;
      value: number;
      percentage: number;
    }>;
    dailyTrend: Array<{
      date: string;
      inbound: number;
      outbound: number;
    }>;
  };
  
  // 表格数据
  tableData?: Array<Record<string, any>>;
  
  // 自定义操作按钮
  customActions?: ReactNode;
  
  // 流程图跳转配置
  flowChartModule?: string;
  
  // 物流地图配置
  logisticsMap?: {
    enabled: boolean;
    transferOrders?: TransferOrder[];
    onWarehouseClick?: (warehouse: Warehouse) => void;
    onVehicleClick?: (order: TransferOrder) => void;
  };
}

export function ReportTemplate({
  title,
  subtitle,
  icon: Icon,
  backPath = "/",
  isLoading,
  error,
  onRefresh,
  kpiStats,
  statusInfo,
  chartData,
  tableData,
  customActions,
  flowChartModule,
  logisticsMap
}: ReportTemplateProps) {
  const navigate = useNavigate();


  return (
    <div className="min-h-screen bg-cream overflow-x-auto">
      {/* 页面头部 */}
      <div className="bg-cream-80 border-b border-slate-200">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(backPath)}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-16 font-bold text-slate-900">{title}</h1>
                <p className="text-12 text-slate-600">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {isLoading ? (
                <>
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </>
              ) : (
                <>
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-12">
                    {statusInfo.totalCount || 0}条记录
                  </Badge>
                  {statusInfo.efficiency && (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-12">
                      效率{statusInfo.efficiency}%
                    </Badge>
                  )}
                  {statusInfo.customBadges?.map((badge, index) => (
                    <Badge 
                      key={index}
                      variant={badge.variant}
                      className="text-12"
                    >
                      {badge.label}: {badge.value}
                    </Badge>
                  ))}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onRefresh}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-6">
        {/* 错误处理 */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              数据加载失败: {error.message}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRefresh}
                className="ml-2"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                重试
              </Button>
            </AlertDescription>
          </Alert>
        )}
        
        {/* KPI 卡片 */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-24" />
              </Card>
            ))
          ) : (
            kpiStats.map((stat, index) => (
              <StatCard
                key={index}
                title={stat.title}
                value={stat.value}
                change={stat.change}
                changeType={stat.changeType}
                icon={stat.icon}
                gradient={index === 0}
                onClick={stat.onClick}
              />
            ))
          )}
        </div>

        {/* 物流地图部分 */}
        {logisticsMap?.enabled && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon className="h-5 w-5 mr-2 text-primary" />
                <h2 className="text-16 font-semibold">物流调拨监控</h2>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/logistics')} 
                  className="text-14"
                >
                  查看详情
                </Button>
              </div>
            </div>
            {isLoading ? (
              <Card className="p-6">
                <Skeleton className="h-96 w-full" />
              </Card>
            ) : (
              <EChartsMap
                transferOrders={logisticsMap.transferOrders}
                onWarehouseClick={logisticsMap.onWarehouseClick}
                onVehicleClick={logisticsMap.onVehicleClick}
              />
            )}
          </div>
        )}

        {/* 图表部分 */}
        {chartData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon className="h-5 w-5 mr-2 text-primary" />
                <h2 className="text-16 font-semibold">趋势分析</h2>
              </div>
              <div className="flex items-center space-x-2">
                {flowChartModule && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/flow-chart?module=${flowChartModule}`)} 
                    className="text-14"
                  >
                    查看流程图
                  </Button>
                )}
                {customActions}
              </div>
            </div>
            {isLoading ? (
              <Card className="p-6">
                <Skeleton className="h-48 w-full" />
              </Card>
            ) : (
              <EChartsSection data={chartData} />
            )}
          </div>
        )}

        {/* 数据表格 */}
        {tableData && (
          <div className="space-y-6">
            {isLoading ? (
              <Card className="p-6">
                <Skeleton className="h-64 w-full" />
              </Card>
            ) : (
              <DataTable data={tableData} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}